const React = require('react');
const InvalidSubmitUtils = require('./invalidSubmitUtils');
const PreferencesSubmitUtils = require('./preferencesSubmitUtils');
const ValidSubmitUtils = require('./validSubmitUtils');

const {
    useCallback
} = React;

const internals = {};

internals.isEventLike = (value) => {

    return !!value
        && typeof value === 'object'
        && (
            typeof value.preventDefault === 'function'
            || typeof value.stopPropagation === 'function'
            || typeof value.nativeEvent !== 'undefined'
            || typeof value.target !== 'undefined'
            || typeof value.currentTarget !== 'undefined'
        );
};

internals.normalizeSubmitOptions = (options) => {

    if (internals.isEventLike(options)) {
        return {};
    }

    return options || {};
};


module.exports = function useUserInputsSubmitController({
    fields,
    formMethods,
    getFieldValue,
    getRendererStateSlice,
    getSignupDetails,
    inputRefs = {},
    onInvalidSubmit,
    preferencesHandle,
    props,
    setRendererState,
    validate
}) {

    const submit = useCallback((options) => {
        const normalizedOptions = internals.normalizeSubmitOptions(options);

        const {
            shouldRedirect = true,
            values,
            successRedirectPath: optionsSuccessRedirectPath
        } = normalizedOptions;

        const {
            croppedPicture,
            successRedirectPath: rendererSuccessRedirectPath
        } = getRendererStateSlice(['croppedPicture', 'successRedirectPath']);
        const successRedirectPath = optionsSuccessRedirectPath || rendererSuccessRedirectPath;

        const {
            hasErrors,
            userPreferencesHasErrors
        } = PreferencesSubmitUtils.buildSubmitValidationState({
            fields,
            validate,
            userPreferences: preferencesHandle?.current
        });
        const invalidFields = fields.filter(validate);

        if (InvalidSubmitUtils.handleInvalidSubmitIfNeeded({
            hasErrors,
            userPreferencesHasErrors,
            fields,
            formMethods,
            validate,
            housing: inputRefs.housing,
            refs: inputRefs,
            specifyGenderTextfield: inputRefs.specifyGenderTextfield,
            setUiState: setRendererState,
            onInvalidSubmit
        })) {
            return;
        }

        const validSubmitOptions = ValidSubmitUtils.buildValidSubmitOptions({
            existingPicture: props.signupDetails?.croppedPicture || props.signupDetails?.picture || null,
            getFieldValue,
            croppedPicture,
            saveProfilePicture: props.saveProfilePicture,
            shouldRedirect,
            values,
            successRedirectPath,
            getSignupDetails,
            onSubmit: props.onSubmit,
            onSubmitNoRedirect: props.onSubmitNoRedirect,
            stillExists: props.stillExistsRef?.current !== false,
            setUiState: setRendererState
        });

        ValidSubmitUtils.submitWhenValid({
            setUiState: setRendererState,
            performValidSubmit: async () => ValidSubmitUtils.performValidSubmit(validSubmitOptions)
        });
    }, [
        fields,
        getFieldValue,
        getRendererStateSlice,
        getSignupDetails,
        inputRefs,
        onInvalidSubmit,
        preferencesHandle,
        props,
        setRendererState,
        validate
    ]);

    const submitForm = useCallback((options) => {

        const normalizedOptions = internals.normalizeSubmitOptions(options);
        const hasExplicitSubmitOptions = Object.keys(normalizedOptions).length > 0;

        if (!hasExplicitSubmitOptions && typeof formMethods?.handleSubmit === 'function') {
            return formMethods.handleSubmit(
                (values) => submit({
                    ...normalizedOptions,
                    values
                }),
                () => submit(normalizedOptions)
            )();
        }

        return submit({
            ...normalizedOptions,
            values: normalizedOptions.values || formMethods?.getValues?.()
        });
    }, [formMethods, submit]);

    return {
        submit: submitForm
    };
};

module.exports.internals = internals;
